import React, { useState } from 'react'
import { Button } from '@mobi/component-library/Common/V2/'
import { Input, Password } from '@mobi/component-library/Common/V2/Inputs'
import styled from '@emotion/styled'

import { spacing } from '@mobi/component-library/Theme/Common'
import { handleMfaError } from '../helpers'
import { MfaError } from '@mobi/api-types'
import { authenticateWithMfa } from '../Services/mfaService'

interface PasswordVerificationProps {
  onVerify: () => void
  onCancel: () => void
  accountNumber: number
}

type MfaChallengeInformation = {
  mfaSessionId: string
  mfaFactor: 'SMS' | 'EMAIL'
  mfaHint: string
}

export const PasswordVerification: React.FC<PasswordVerificationProps> = ({
  onVerify,
  onCancel,
  accountNumber,
}) => {
  const [password, setPassword] = useState('')
  const [mfaCode, setMfaCode] = useState('')

  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const isError = !!error

  const [challenge, setChallenge] = useState<MfaChallengeInformation | null>(null)

  const handleVerify = async () => {
    if (!password) {
      setError('Please enter your password')
      return
    }

    try {
      setIsLoading(true)

      const payload = !challenge
        ? { accountNumber, password: password }
        : {
            accountNumber,
            mfaSessionId: challenge.mfaSessionId,
            mfaFactor: challenge.mfaFactor,
            mfaCode,
          }

      const result = await authenticateWithMfa(payload)

      if (result.IsSuccessful) {
        onVerify()
      } else if (result.MustCompleteMfaChallenge) {
        setChallenge({
          mfaSessionId: result.MfaSessionId,
          mfaFactor: result.MfaFactor,
          mfaHint: result.MfaHint,
        })
      }
    } catch (e) {
      handleMfaError(e as MfaError, 'verification')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <ContainerStyled>
      {isError && <p>Something went wrong. Please try again.</p>}
      <p>
        Please enter your password to manage your TABtouch account multi-factor authentication (MFA)
        settings.
      </p>

      {challenge == null ? (
        <Password
          id='password'
          name='password'
          label='Password'
          value={password}
          onChange={e => setPassword(e.target.value)}
          isInvalid={!!error}
          helpText={error || undefined}
          onFocus={() => setError('')}
        />
      ) : (
        <Input
          name='mfa-code'
          label={`Enter code sent to ${challenge.mfaHint}`}
          value={mfaCode}
          onChange={e => setMfaCode(e.target.value)}
        />
      )}

      <ButtonContainerStyled>
        <Button onClick={handleVerify} color='primary' disabled={!password} isLoading={isLoading}>
          Continue
        </Button>
        <Button onClick={onCancel} color='tertiary_grey'>
          Cancel
        </Button>
      </ButtonContainerStyled>
    </ContainerStyled>
  )
}

// ======
// Styles
// ======

const ContainerStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.md,
  maxWidth: '80em',
  margin: '0 auto',
  marginTop: '-1rem',
  p: {
    textAlign: 'left',
  },
})

const ButtonContainerStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.md,
  marginTop: spacing.sm,
})
